import * as React from 'react'

import { IFilesService } from '../../interfaces/services/files'

import { Creative } from '../../interfaces/entities/creative'
import DI from '../../app/di'
import { translateWithUserLocale } from '../../utils/i18n'

import {
  Box,
  Typography,
  Paper,
  Divider,
  Icon,
  Tooltip,
  Link,
  Button,
  styled,
} from '@design/pt-ui'
import { ICreativeService } from '../../interfaces/services/creatives'
import GeneratePreview from './GeneratePreview'

const Root = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  textAlign: 'left',
  marginTop: theme.spacing(1),
  marginRight: theme.spacing(1),
  width: '100%',
  top: '-10px',
  minHeight: '30vh',
  '@media (max-width: 960px)': {
    position: 'relative',
    width: 'auto',
    margin: 24,
  },
}))

const Preview = styled('div', {
  shouldForwardProp: (prop) => prop !== 'outbrain',
})<{ outbrain?: boolean }>(({ outbrain }) => ({
  marginTop: 45,
  display: 'flex',
  ...(outbrain && { flexDirection: 'column' }),
}))

const PreviewRight = styled('div', {
  shouldForwardProp: (prop) => prop !== 'outbrain',
})<{ outbrain?: boolean }>(({ outbrain }) => ({
  marginLeft: outbrain ? 0 : '30px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  ...(outbrain && { width: '300px' }),
}))

export interface Props {
  creative: Creative
  preview?: boolean
  publicRoute?: boolean
}

export function CreativePreview(props: Props) {
  const getCreativeService = () => {
    return DI.get('CreativeService') as ICreativeService
  }

  const getContent = () => {
    const filesService = DI.get('FilesService') as IFilesService
    const preview = filesService.getFileUrl(
      props.creative.data.file,
      props.publicRoute || false,
    )

    const paPlacements = getCreativeService().getFormatsetPlacementForCreative(
      props.creative,
      'performance_advertising',
    )

    if (paPlacements === 'performance_advertising_outbrain') {
      return (
        <Preview outbrain={true}>
          <div>
            <img
              style={{ width: '300px', height: 'auto' }}
              src={preview}
              alt=""
            />
          </div>
          <PreviewRight outbrain={true}>
            <div style={{ height: '80%' }}>
              <h2>{props.creative.data.headline}</h2>
              <p>{props.creative.data.text}</p>
            </div>
            <Link color="textSecondary" sx={{ height: '20%' }}>
              {props.creative.data.link_url}
            </Link>
          </PreviewRight>
        </Preview>
      )
    }

    return (
      <Preview id="cp-preview">
        {props.creative.type.name === 'video' ? (
          <video
            style={{ width: 'auto', height: '150px' }}
            src={preview}
            controls>
            <source src={preview} type="video/mp4" />
          </video>
        ) : (
          <img
            style={{ width: 'auto', height: '150px' }}
            src={preview}
            alt=""
          />
        )}
        <PreviewRight>
          <div>
            <h2>{props.creative.data.headline}</h2>
            <p style={{ margin: 0 }}>{props.creative.data.text}</p>
          </div>
          <Link color="textSecondary" sx={{ marginBottom: '10px' }}>
            {props.creative.data.link_text}
          </Link>
        </PreviewRight>
      </Preview>
    )
  }

  return (
    <Root>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '15px',
          marginTop: '8px',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6">
            {translateWithUserLocale(
              'creative_details.live_preview_from_performance_advertising',
            )}
          </Typography>
          <Tooltip
            placement="top"
            title={translateWithUserLocale(
              'preview.description_performance_advertising',
            )}>
            <Icon
              sx={{
                marginLeft: '15px',
                color: 'grey',
              }}>
              info
            </Icon>
          </Tooltip>
        </Box>
        {!props.preview && (
          <GeneratePreview
            activator={
              <Button variant="outlined" color="inherit">
                {translateWithUserLocale('preview.generate_preview')}
              </Button>
            }
            creative={props.creative}
          />
        )}
      </Box>
      <Divider component="h3" />
      {getContent()}
    </Root>
  )
}

export default CreativePreview
